/**
 * useApproval Hook
 *
 * Simplified hook for handling post approval and rejection using status-based workflow.
 * Follows the same pattern as useDraftGeneration:
 * 1. Update status to "Approving"/"Rejecting"
 * 2. Trigger webhook
 * 3. Poll until status changes to "Posted"/"Rejected"
 */

import { useState, useCallback, useRef } from "react";
import { triggerApproval, triggerRejection, requestEdit as requestEditApi } from "../api/webhook";
import { updatePostStatus, getBlogPostById } from "../api/supabase";

/**
 * Hook for managing post approval and rejection actions
 * @returns {{
 *   approvePost: Function,
 *   rejectPost: Function,
 *   requestEdit: Function,
 *   loading: boolean,
 *   loadingStage: string,
 *   success: boolean,
 *   error: string|null,
 *   successData: Object|null,
 *   resetState: Function
 * }}
 */
export const useApproval = () => {
  const [loading, setLoading] = useState(false);
  const [loadingStage, setLoadingStage] = useState("idle");
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [successData, setSuccessData] = useState(null);

  // Move useRef to top level of hook
  const pollingIntervalRef = useRef(null);

  const resetState = useCallback(() => {
    setLoading(false);
    setLoadingStage("idle");
    setSuccess(false);
    setError(null);
    setSuccessData(null);
    // Clear any active polling when resetting
    if (pollingIntervalRef.current) {
      clearTimeout(pollingIntervalRef.current);
    }
  }, []);

  const approvePost = useCallback(async (post) => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(false);
      setLoadingStage("starting");

      console.log("Starting approval process for post:", post.id);

      // Step 1: Update status to "Approving"
      setLoadingStage("updating-status");
      await updatePostStatus(post.id, "Approving");
      console.log("Post status updated to 'Approving'");

      // Step 2: Trigger webhook — rollback status if it fails so post reappears in Drafts
      setLoadingStage("triggering-workflow");
      let webhookResult;
      try {
        webhookResult = await triggerApproval(post.id);
      } catch (webhookErr) {
        await updatePostStatus(post.id, "Pending Approval");
        throw webhookErr;
      }

      if (!webhookResult.success) {
        await updatePostStatus(post.id, "Pending Approval");
        throw new Error(`Webhook failed: ${webhookResult.error}`);
      }

      console.log("Approval workflow triggered successfully");

      // Step 3: Poll for completion
      setLoadingStage("processing");

      const pollForCompletion = () => {
        return new Promise((resolve, reject) => {
          const startTime = Date.now();
          const maxDuration = 5 * 60 * 1000; // 5 minutes

          const checkStatus = async () => {
            try {
              const updatedPost = await getBlogPostById(post.id);

              if (updatedPost?.status === "Posted") {
                console.log("Post approved and published successfully");
                resolve(updatedPost);
                return;
              }

              if (updatedPost?.status === "Rejected") {
                reject(new Error("Post was rejected during processing"));
                return;
              }

              // Check timeout
              if (Date.now() - startTime > maxDuration) {
                reject(new Error("Approval process timed out after 5 minutes"));
                return;
              }

              // Continue polling
              pollingIntervalRef.current = setTimeout(checkStatus, 5000); // Increased from 3000 to 5000ms
            } catch (error) {
              reject(error);
            }
          };

          checkStatus();
        });
      };

      const finalPost = await pollForCompletion();

      setLoadingStage("complete");
      setSuccess(true);
      setSuccessData({
        post: finalPost,
        linkedin_url: finalPost["LinkedIn Post URL"] || "",
        x_url: finalPost["X Post URL"] || "",
        threads_url: finalPost["Threads Post URL"] || "",
      });

      return { success: true, data: finalPost };
    } catch (err) {
      console.error("Error in approval process:", err);
      setLoadingStage("error");
      setError(err.message);
      setSuccess(false);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
      if (pollingIntervalRef.current) {
        clearTimeout(pollingIntervalRef.current);
      }
    }
  }, []);

  const rejectPost = useCallback(async (postId) => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(false);
      setLoadingStage("starting");

      console.log("Starting rejection process for post:", postId);

      // Step 1: Update status to "Rejecting"
      setLoadingStage("updating-status");
      await updatePostStatus(postId, "Rejecting");
      console.log("Post status updated to 'Rejecting'");

      // Step 2: Trigger webhook — rollback status if it fails so post reappears in Drafts
      setLoadingStage("triggering-workflow");
      let webhookResult;
      try {
        webhookResult = await triggerRejection(postId);
      } catch (webhookErr) {
        await updatePostStatus(postId, "Pending Approval");
        throw webhookErr;
      }

      if (!webhookResult.success) {
        await updatePostStatus(postId, "Pending Approval");
        throw new Error(`Webhook failed: ${webhookResult.error}`);
      }

      console.log("Rejection workflow triggered successfully");

      // Step 3: Poll for completion
      setLoadingStage("processing");

      const pollForCompletion = () => {
        return new Promise((resolve, reject) => {
          const startTime = Date.now();
          const maxDuration = 2 * 60 * 1000; // 2 minutes for rejection

          const checkStatus = async () => {
            try {
              const updatedPost = await getBlogPostById(postId);

              if (updatedPost?.status === "Rejected") {
                console.log("Post rejected successfully");
                resolve(updatedPost);
                return;
              }

              // Check timeout
              if (Date.now() - startTime > maxDuration) {
                reject(new Error("Rejection process timed out after 2 minutes"));
                return;
              }

              // Continue polling
              pollingIntervalRef.current = setTimeout(checkStatus, 3000); // Keep at 3 seconds for rejection (faster)
            } catch (error) {
              reject(error);
            }
          };

          checkStatus();
        });
      };

      const finalPost = await pollForCompletion();

      setLoadingStage("complete");
      setSuccess(true);
      setSuccessData({ post: finalPost });

      return { success: true, data: finalPost };
    } catch (err) {
      console.error("Error in rejection process:", err);
      setLoadingStage("error");
      setError(err.message);
      setSuccess(false);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
      if (pollingIntervalRef.current) {
        clearTimeout(pollingIntervalRef.current);
      }
    }
  }, []);

  const requestEdit = useCallback(async (postId, editNotes) => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(false);
      setLoadingStage("starting");

      // Update Supabase with edit request
      await updatePostStatus(postId, "Needs Edit", {
        "Edit Notes": editNotes,
      });

      // Call webhook for notification
      await requestEditApi(postId, editNotes);

      setLoadingStage("complete");
      setSuccess(true);

      return { success: true };
    } catch (err) {
      console.error("Error requesting edit:", err);
      setLoadingStage("error");
      setError(err.message);
      setSuccess(false);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    approvePost,
    rejectPost,
    requestEdit,
    loading,
    loadingStage,
    success,
    error,
    successData,
    resetState,
  };
};

export default useApproval;
